import { Component, Input, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { take } from 'rxjs/operators';

import {
  NotificationService,
  EBSService,
  AssignmentsCountersService,
  WebsocketService,
  AssignmentService,
  HelperService,
  OverviewTableService
} from '@shared/services';
import { TableHeaderEntryModel, TableBodyEntryModel } from '@shared/models';
import { Assignment, AssignmentExtended } from '@shared/factories';

@Component({
  selector: 'ebs-table',
  templateUrl: '../../table-entries.component.html',
  styleUrls: ['./ebs-table.component.sass'],
  host: { class: 'am-flex-column bg-white b-radius-wide overflow' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EbsTableComponent {
  @Input() rows:         AssignmentExtended[] | Assignment[];
  @Input() paginationId: string;
  @Input() loading:      boolean;

  @Output() headerCallback = new EventEmitter<any>();
  @Output() updateData     = new EventEmitter<any>();
  constructor(
    private assignmentService:          AssignmentService,
    private assignmentsCountersService: AssignmentsCountersService,
    private overviewTableService:       OverviewTableService,
    private ebsService:                 EBSService,
    private websocketService:           WebsocketService,
    private notificationService:        NotificationService,
    private helperService:              HelperService,
  ) { }

  headerCallbackHandler(res) {
    this.headerCallback.emit(res);
  }

  getTableClass(): string {
    return 'ebs-list-wrapper';
  }

  getOptionalValue(field: string): boolean {
    return this[field] || null;
  }

  prepareTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Mitarbeiter', class: 'name-cell',  sort_by: ['external_employee_last_name', 'external_employee_first_name']  },
      { title: 'Einspiel ID', class: 'date-cell',  sort_by: ['ebs_id']                                                       },
      { title: 'Einsätze',    class: 'title-cell', sort_by: ['title']                                                        },
      { title: 'Aktion',      class: 'action-cell'                                                                           }
    ];
  }

  private preparePreload(): TableBodyEntryModel[] {
    return new Array<TableBodyEntryModel>(10).fill({
      cells: [
        this.overviewTableService.getExternalEmployeeCellPlaceholder(),
        { xs_label: ' ', class: 'date-cell',  value: ' ' },
        { xs_label: ' ', class: 'title-cell', children: [
          { class: 'am-flex-center width-xl   height-xl loading-animate' },
          { class: 'am-flex-1      width-full height-xl loading-animate' },
          { class: 'am-flex-center width-xl   height-xl loading-animate' }
        ]},
        { class: 'action-cell', buttons: [
          { class: 'width-36 height-36' },
          { class: 'action-cell-button' }
        ]}
      ]
    });
  }

  prepareTableBody(): TableBodyEntryModel[] {
    if (this.loading) return this.preparePreload();
    return this.rows.map((assignment: AssignmentExtended): TableBodyEntryModel => ({
      cells: [
        this.overviewTableService.getExternalEmployeeCell(assignment.external_employee),
        { xs_label: 'Einspiel ID', class: 'date-cell',     value: assignment.latestEBS.id },
        { xs_label: 'Einsätze',    class: 'title-cell',    children: [{
          class: 'ebs-history-wrapper', children: [
            { class: 'am-flex-column',   children: [
              { class: 'history-item', children: [
                { class: assignment.latestEBS.confirmed_at ? 'icon-tick' : 'icon-cross' },
                { class: `history-item-name ${assignment.confirmed ? '' : 'needs-confirmation'}`, routerLink: ['/time-tracking/ebs-details', assignment.id, assignment.latestEBS.id], value: `${assignment.title} v${assignment.latestEBS.version}` },
              ]},
              { class:'history-container', skip: !assignment.history.length, children: assignment.history.map(e => ({
                  class: 'history-item old', children: [
                    { class: e.confirmed_at ? 'icon-tick' : 'icon-cross' },
                    { class: `history-item-name ${e.confirmed ? '' : 'needs-confirmation'}`, routerLink: ['/time-tracking/ebs-details', assignment.id, e.id], value: `${assignment.title} v${e.version}` }
                ]
              }))}
            ]},
            { class: `icon-arrow-down ${assignment.history.length ? '' : 'hide'}`, click: (event) => this.collapseHistory(event) }
          ]},
        ]},
        { class: 'action-cell',
          buttons: [
            {
              class: 'font-small-icon tooltip-hover blue-thin-border am-flex-center xs-hide',
              icon: `${assignment.latestEBS.archived_at ? 'icon-rotate-ccw'  : 'icon-archive m0'}`,
              tooltip: assignment.latestEBS.archived_at ? 'Wiederherstellen' : 'Archivieren',
              click: () => assignment.latestEBS.archived_at ? this.manualUnArchive(assignment) : this.manualArchive(assignment)
            },
            {
              class: 'blue-thin-border custom-action-cell-button xs-show-table',
              icon: `${assignment.latestEBS.archived_at ? 'icon-rotate-ccw'  : 'icon-archive'} am-flex-center  m0`,
              label:   assignment.latestEBS.archived_at ? 'Wiederherstellen' : 'Archivieren',
              click: () => assignment.latestEBS.archived_at ? this.manualUnArchive(assignment) : this.manualArchive(assignment)
            },
            {
              class: 'action-cell-button',
              label: 'Anrufen',
              icon: 'icon-phone',
              click: () => this.helperService.callExternal(assignment.external_employee.phone_number)
            }
          ]
        }
      ]
    }));
  }

  private collapseHistory(e): void {
    let historyWrapper = e.target.previousSibling.getElementsByClassName('history-container')[0];
    if (historyWrapper.classList.contains('collapsed')) historyWrapper.style.height = '0px';
    else {
      let dd = `${Array.from(historyWrapper.children).map((el: any) => el.offsetHeight).reduce((sum, val) => sum += val + 5, 0)}px`;
      historyWrapper.style.height = dd;
    }
    e.target.classList.toggle('collapsed');
    historyWrapper.classList.toggle('collapsed');
  }

  private manualArchive(assignment: AssignmentExtended): void {
    this.notificationService.confirm('Bestätigung', 'Möchten Sie diese EBS Version und alle vorherigen archivieren ?', () => {
      this.notificationService.wait();
      this.ebsService.archiveEBS(assignment.latestEBS.id).pipe(take(1)).subscribe(
        res   => this.refreshEBSData(assignment),
        error => this.notificationService.alert(error.message || error)
      )
    });
  }

  private manualUnArchive(assignment: AssignmentExtended): void {
    this.notificationService.confirm('Bestätigung', 'Möchten Sie diese EBS Version wiederherstellen ?', () => {
      this.notificationService.wait();
      this.ebsService.unArchiveEBS(assignment.latestEBS.id).pipe(take(1)).subscribe(
        res   => this.refreshEBSData(),
        error => this.notificationService.alert(error.message || error)
      )
    });
  }

  private refreshEBSData(assignment: AssignmentExtended = null): void {
    if  (this.paginationId === 'archive') this.updateData.emit();
    else if (assignment?.confirmed) this.assignmentService.forceReload();
    else this.notificationService.close();
    this.reloadCounters();
  }

  private reloadCounters(): void {
    if (!this.websocketService.wsOnline()) this.assignmentsCountersService.reloadEBSCounter();
  }

}
